import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import ModifierCustomiser from "../customers/ModifierCustomiser";

const DEFAULT_IMAGE = "/default-product.png";

const ItemList = ({ filteredItems, addToOrder, customizerProduct, setCustomizerProduct, isLargeBtn, isListView, navZone, navItemIndex, setNavItemIndex, setNavZone }) => {

  const [displayCount, setDisplayCount] = useState(500);
  const observerRef = useRef(null);
  const observerTimeoutRef = useRef(null);

  useEffect(() => {
    setDisplayCount(500);
    // Optionally, we could scroll the window/container to top here if needed
  }, [filteredItems]);

  const lastItemRef = useCallback(
    (node) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node) return;
      
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (observerTimeoutRef.current) clearTimeout(observerTimeoutRef.current);
          observerTimeoutRef.current = setTimeout(() => {
            setDisplayCount((prev) => {
              if (prev >= filteredItems.length) return prev;
              return prev + 500;
            });
          }, 100); // 100ms debounce to prevent rapid cascade rendering
        }
      });
      
      observerRef.current.observe(node);
    },
    [filteredItems.length]
  );

  const handleProductClick = (item) => {
    const hasVariants = item.variants && item.variants.length > 0;
    const hasAddOns = item.addOns && item.addOns.length > 0;

    if (hasVariants || hasAddOns) {
      // Open the modifier customiser
      setCustomizerProduct(item);
    } else {
      // No modifiers — add directly
      addToOrder(item, null, []);
    }
  };

  const handleCustomizerAdd = (product, selectedVariant, selectedAddOns) => {
    addToOrder(product, selectedVariant, selectedAddOns);
    setCustomizerProduct(null);
  };

  if (filteredItems.length === 0) {
    return (
      <div className="py-10 text-center">
        <p className="text-xl font-semibold text-gray-500 mb-3">
          No products available
        </p>
        <Link to="/add-product" className="text-teal-600 font-semibold hover:underline">
          Add Products
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className={isListView ? "flex flex-col gap-2" : "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"}>
        {filteredItems.slice(0, displayCount).map((item, index) => {
          const hasModifiers =
            (item.variants && item.variants.length > 0) ||
            (item.addOns && item.addOns.length > 0);
          
          const isFocused = navZone === "items" && navItemIndex === index;
          const focusStyle = isFocused ? "ring-2 ring-teal-300 border-teal-300 bg-teal-50/30 scale-[1.02] shadow-lg z-10" : "";

          if (isListView) {
            return (
              <div
                ref={index === Math.min(displayCount, filteredItems.length) - 1 ? lastItemRef : null}
                key={item._id || item.productName}
                data-nav-item={index}
                onClick={() => {
                  setNavItemIndex(index);
                  setNavZone("items");
                  handleProductClick(item);
                }}
                className={`group bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-teal-400/50 transition-all duration-300 flex items-center justify-between p-3 cursor-pointer ${focusStyle}`}
              >
                <div className="flex-1 min-w-0 pr-4">
                  <div className="flex items-center gap-2 mb-1">
                    {item.type && item.type.toLowerCase() !== "not_defined" && (
                      <span
                        className={`px-1.5 py-0.5 text-[9px] font-bold rounded text-white shadow-sm shrink-0 ${
                          item.type.toLowerCase() === "veg"
                            ? "bg-emerald-600"
                            : item.type.toLowerCase() === "non-veg"
                            ? "bg-rose-600"
                            : "bg-slate-600"
                        }`}
                      >
                        {item.type.toLowerCase() === "veg"
                          ? "V"
                          : item.type.toLowerCase() === "non-veg"
                          ? "NV"
                          : item.type.toUpperCase().charAt(0)}
                      </span>
                    )}
                    <h3 className="text-sm font-bold text-slate-800 truncate" title={item.productName}>
                      {item.productName}
                    </h3>
                  </div>
                  {hasModifiers && (
                    <span className="inline-block bg-indigo-100 text-indigo-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider mt-0.5">
                      Customisable
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <p className="text-teal-600 font-extrabold text-sm">₹{item.price}</p>
                  <button
                    data-nav-add="true"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNavItemIndex(index);
                      setNavZone("items");
                      handleProductClick(item);
                    }}
                    className={`text-white rounded-full flex items-center justify-center font-black shadow-sm transition-all duration-200 hover:scale-105 active:scale-95 ${
                      isLargeBtn ? "w-10 h-10 text-xl" : "w-7 h-7 text-sm"
                    } ${
                      hasModifiers
                        ? "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/10"
                        : "bg-teal-600 hover:bg-teal-700 shadow-teal-600/10"
                    }`}
                    title={hasModifiers ? "Customise & Add" : "Add to cart"}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          }

          return (
            <div
              ref={index === Math.min(displayCount, filteredItems.length) - 1 ? lastItemRef : null}
              key={item._id || item.productName}
              data-nav-item={index}
              onClick={() => {
                setNavItemIndex(index);
                setNavZone("items");
                handleProductClick(item);
              }}
              className={`group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200/80 transition-all duration-300 overflow-hidden flex flex-col justify-between cursor-pointer ${focusStyle}`}
            >
              <div>
                <div className="relative h-28 bg-slate-50 overflow-hidden">
                  <img
                    src={item.image || item.category?.image || DEFAULT_IMAGE}
                    alt={item.productName}
                    onError={(e) => (e.target.src = DEFAULT_IMAGE)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />

                  {item.type && item.type.toLowerCase() !== "not_defined" && (
                    <span
                      className={`absolute top-2 right-2 px-2 py-0.5 text-[10px] font-bold rounded-lg text-white shadow-sm ${
                        item.type.toLowerCase() === "veg"
                          ? "bg-emerald-600"
                          : item.type.toLowerCase() === "non-veg"
                          ? "bg-rose-600"
                          : "bg-slate-600"
                      }`}
                    >
                      {item.type.toLowerCase() === "veg"
                        ? "VEG"
                        : item.type.toLowerCase() === "non-veg"
                        ? "NON-VEG"
                        : item.type.toUpperCase()}
                    </span>
                  )}

                  {/* Modifier indicator badge */}
                  {hasModifiers && (
                    <span className="absolute bottom-2 left-2 bg-indigo-600/90 backdrop-blur-sm text-white text-[9px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider shadow-sm">
                      Customisable
                    </span>
                  )}
                </div>

                <div className="px-3 pt-3">
                  <p className="text-sm font-semibold text-slate-800 group-hover:text-teal-600 transition duration-150 break-words leading-tight" title={item.productName}>
                    {item.productName}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-3">
                <p className="text-teal-600 font-extrabold text-sm">₹{item.price}</p>

                <button
                  data-nav-add="true"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNavItemIndex(index);
                    setNavZone("items");
                    handleProductClick(item);
                  }}
                  className={`text-white rounded-full flex items-center justify-center font-black shadow-sm transition-all duration-200 hover:scale-105 active:scale-95 ${
                    isLargeBtn ? "w-12 h-12 text-2xl" : "w-7 h-7 text-sm"
                  } ${
                    hasModifiers
                      ? "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/10"
                      : "bg-teal-600 hover:bg-teal-700 shadow-teal-600/10"
                  }`}
                  title={hasModifiers ? "Customise & Add" : "Add to cart"}
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modifier Customiser Modal */}
      <ModifierCustomiser
        product={customizerProduct}
        isOpen={!!customizerProduct}
        onClose={() => setCustomizerProduct(null)}
        onAdd={handleCustomizerAdd}
      />
    </>
  );
};

export default ItemList;
